/** The web half of project admission for a host with native code (iOS NativeNav, the desktop's
 *  Tauri port): installs and clears the admitted scope through `platform.projectAdmission.native`,
 *  and keeps the owner/sequence proof requests carry. */
import { getPlatform } from "../platform/platformRef";
import type { NativeAdmissionPort } from "../platform/Platform";
import { isNativeNavAvailable } from "./nativeNav";
import { isTenantScopeRequired } from "../contexts/tenantScopeHeaders";
import { expireTabCompanySession, revalidateTenantSession } from "../contexts/tabCompanySession";
import type { NativeAdmittedScope } from "../platform/admittedScope";

export class NativeTenantScopeUnsupportedError extends Error {
    constructor() {
        super(
            "This app version cannot open the selected company. Continue with your existing project or open it in your browser.",
        );
        this.name = "NativeTenantScopeUnsupportedError";
    }
}

let ownerId: string | null = null;
let sequence = 0;
let operationRevision = 0;
let supported: Promise<void> | null = null;
let canonicalAttempt = false;
let admittedProof: { ownerId: string; sequence: number } | null = null;
let listenerReady: Promise<unknown> | null = null;

/** The host's native half of an admitted project; none in a browser. */
function nativePort(): NativeAdmissionPort | undefined {
    return getPlatform().projectAdmission.native;
}

function documentOwner(): string {
    ownerId ??= Array.from(crypto.getRandomValues(new Uint32Array(4)), (value) =>
        value.toString(16).padStart(8, "0"),
    ).join("");
    return ownerId;
}

/** Called only before an explicit canonical transition; shipped legacy bridges remain untouched. */
export async function ensureNativeTenantScopeSupport(urlRouting = false): Promise<void> {
    // A host that has not declared canonical selection still binds one legacy project to
    // its native session and durable caches: never treat its missing native port as
    // browser support for a company/project credential. A URL route's credential needs a
    // host that runs on project URLs, which only it declares.
    const { supportsCanonicalSelection, supportsUrlRouting } = getPlatform().projectAdmission;
    if (!supportsCanonicalSelection || (urlRouting && !supportsUrlRouting))
        throw new NativeTenantScopeUnsupportedError();
    const port = nativePort();
    if (!port) return;
    supported ??= port
        .capabilities()
        .then(async ({ version }) => {
            if (version !== 1) throw new NativeTenantScopeUnsupportedError();
            canonicalAttempt = true;
            await port.clear({ ownerId: documentOwner(), sequence: ++sequence });
        })
        .catch(() => {
            throw new NativeTenantScopeUnsupportedError();
        });
    await supported;
}

export function nativeScopeRequestProof(): { ownerId: string; sequence: number } | undefined {
    if (!isTenantScopeRequired()) return undefined;
    if (!admittedProof) throw new Error("Native project admission is required");
    return admittedProof;
}

/** The proof the legacy iOS NativeNav bridge's requests carry (other hosts send none).
 *  Additional fields are ignored by old binaries on unchanged legacy routes. */
export function nativeContextProof() {
    if (!isNativeNavAvailable()) return {};
    const proof = nativeScopeRequestProof();
    return proof
        ? { scopeMode: "canonical", scopeOwnerId: proof.ownerId, scopeSequence: proof.sequence }
        : { scopeMode: "legacy", scopeOwnerId: documentOwner(), scopeSequence: ++sequence };
}

export async function installAdmittedNativeScope(admission: NativeAdmittedScope): Promise<void> {
    if (!getPlatform().projectAdmission.supportsCanonicalSelection)
        throw new NativeTenantScopeUnsupportedError();
    const port = nativePort();
    if (!port) return;
    const revision = ++operationRevision;
    await ensureNativeTenantScopeSupport();
    if (revision !== operationRevision) throw new Error("Native project admission superseded");
    const owner = documentOwner();
    const attempt = ++sequence;
    admittedProof = null;
    listenerReady ??= port.onInvalidated((event) => {
        if (event.ownerId !== owner) return;
        // The host dropped this admission, so the credential is spent whatever its deadline
        // says: a renewal that fails now shows the outage instead of passing silently.
        expireTabCompanySession();
        void revalidateTenantSession().catch(() => undefined);
    });
    await listenerReady;
    if (attempt !== sequence) throw new Error("Native project admission superseded");
    const installed = await port.install({
        ...admission,
        ownerId: owner,
        sequence: attempt,
        // The host's own backend: the desktop's configured environment, iOS's native origin.
        apiOrigin: getPlatform().bootstrap.apiOrigin,
    });
    if (attempt !== sequence) throw new Error("Native project admission superseded");
    if (installed?.engineAdmissionToken) {
        const { rebindConversationEngine } = await import("./conversationEngine");
        if (attempt !== sequence) throw new Error("Native project admission superseded");
        await rebindConversationEngine(installed.engineAdmissionToken);
        if (attempt !== sequence) throw new Error("Native project admission superseded");
    }
    admittedProof = { ownerId: owner, sequence: attempt };
}

export async function clearAdmittedNativeScope(options?: {
    revokeOffline?: boolean;
}): Promise<void> {
    operationRevision++;
    admittedProof = null;
    const port = nativePort();
    if (!port || !canonicalAttempt) return;
    await port.clear({
        ownerId: documentOwner(),
        sequence: ++sequence,
        ...(options?.revokeOffline ? { revokeOffline: true } : {}),
    });
}

/** A Document that goes away (a reload into another project, a closed window) takes its
 *  admission with it, so the host's native code never keeps working in a project no page
 *  holds any more. */
function clearOnPageHide(): void {
    let port: NativeAdmissionPort | undefined;
    try {
        port = nativePort();
    } catch {
        return; // no platform installed yet: nothing was admitted
    }
    if (port) void clearAdmittedNativeScope().catch(() => undefined);
}

// Guarded because this module is imported outside a browser: the desktop
// viewer's Node selftest lane pulls it in through `nativeViewer.ts`, and an
// unguarded `window` here throws at import time before any test can run.
if (typeof window !== "undefined") window.addEventListener("pagehide", clearOnPageHide);
